/**
 * Public site layout (Story 20.1; header nav + CTA + mobile menu added in S20.18)
 *
 * Lightweight chrome for anonymous public surfaces (front page, Originals
 * catalog + story + character pages). Unlike AppLayout it does NOT render the
 * logged-in AppHeader nav / AccountMenu.
 *
 * Header (S20.18, prototype-faithful — `shell.jsx` `sm-topbar`/`sm-nav`):
 *   logo · center nav (Etusivu / Esimerkkisatuja / Esimerkkihahmoja, with an
 *   active filled-pill state) · "Kokeile maksutta" CTA · "Kirjaudu" link.
 *   Below `md` the center nav + Kirjaudu collapse behind a hamburger; the logo
 *   and CTA stay in the bar (owner decision 2026-06-07). Hamburger pattern
 *   mirrors AppHeader (CSS breakpoints + useState, no matchMedia).
 *
 * Epic 20 is a Finnish-only landing page: copy here is hardcoded Finnish and
 * deliberately NOT routed through the i18n framework (exception to CLAUDE.md
 * rule 4). The shared Footer keeps its own i18n — only the contactSubject value
 * passed in is a plain literal.
 *
 * @module components/layout/PublicLayout
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Footer } from './Footer';
import { StarField } from '../decorative';

interface PublicLayoutProps {
  /** Page content to render in the main area */
  children: React.ReactNode;
}

/**
 * Finnish subject pre-filled into the footer "Ota yhteyttä" mailto on public
 * surfaces, so enquiries from the landing page are self-labelling.
 */
const PUBLIC_CONTACT_SUBJECT = 'Haluaisin lisätietoja Sagamoonin testikäytöstä';

interface NavItem {
  label: string;
  to: string;
}

/** Center nav (hardcoded Finnish; routes per Epic 20). */
const NAV_ITEMS: NavItem[] = [
  { label: 'Etusivu', to: '/' },
  { label: 'Esimerkkisatuja', to: '/originals' },
  { label: 'Esimerkkihahmoja', to: '/originals/characters' },
];

/**
 * Active-state for a nav item. `/originals/characters` is checked before
 * `/originals` because the character path also starts with `/originals`.
 */
function isNavActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  if (to === '/originals/characters') return pathname.startsWith('/originals/characters');
  if (to === '/originals')
    return pathname.startsWith('/originals') && !pathname.startsWith('/originals/characters');
  return false;
}

export function PublicLayout({ children }: PublicLayoutProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close the mobile menu on Escape (a11y — QA A11Y-002).
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  return (
    <div className="flex min-h-screen flex-col bg-twilight-gradient">
      {/* Subtle star field overlay — same magical register as AppLayout */}
      <StarField starCount={40} />

      <header className="relative z-50">
        <div className="bg-twilight-deep/70 backdrop-blur-md border-b border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-14 items-center justify-between gap-3">
              {/* Logo */}
              <Link
                to="/"
                className="shrink-0 hover:opacity-80 transition-dreamy"
                aria-label="Sagamoon"
              >
                <img
                  src="/images/sagamoon-logo-new-png.png"
                  alt="Sagamoon"
                  className="h-10 w-auto"
                />
              </Link>

              {/* Center nav — desktop only; grouped translucent capsule, active filled pill */}
              <nav
                className="hidden md:flex items-center gap-1 rounded-full bg-white/5 p-1"
                aria-label="Päävalikko"
              >
                {NAV_ITEMS.map((item) => {
                  const active = isNavActive(location.pathname, item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={`inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium transition-dreamy focus:outline-none focus:ring-2 focus:ring-accent-gold/40 ${
                        active
                          ? 'bg-accent-gold/15 text-accent-gold'
                          : 'text-surface-cream/80 hover:text-accent-gold'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              {/* Right cluster */}
              <div className="flex shrink-0 items-center gap-2">
                {/* "Kokeile maksutta" CTA — outlined pill (gold sparing per DESIGN.md) */}
                <button
                  type="button"
                  onClick={() => navigate('/auth/signup')}
                  className="inline-flex min-h-11 items-center rounded-full border-2 border-accent-gold/60 px-4 text-sm font-semibold text-accent-gold transition-dreamy hover:border-accent-gold hover:bg-accent-gold/10 focus:outline-none focus:ring-2 focus:ring-accent-gold/40"
                >
                  Kokeile maksutta
                </button>

                {/* Kirjaudu — desktop bar (in the mobile menu below on small screens) */}
                <Link
                  to="/auth/login"
                  className="hidden md:inline-flex min-h-11 items-center px-2 text-sm font-medium text-surface-cream/80 hover:text-accent-gold transition-dreamy"
                >
                  Kirjaudu
                </Link>

                {/* Hamburger — mobile only */}
                <button
                  type="button"
                  className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-surface-cream hover:bg-white/10 hover:text-accent-gold transition-dreamy"
                  onClick={() => setMobileMenuOpen((open) => !open)}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="public-mobile-menu"
                  aria-label="Avaa valikko"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    {mobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu — nav items + Kirjaudu */}
        {mobileMenuOpen && (
          <nav
            id="public-mobile-menu"
            className="md:hidden absolute left-0 right-0 top-full mx-4 rounded-b-lg border-x border-b border-white/10 bg-twilight-deep/95 shadow-lg backdrop-blur-md"
            aria-label="Mobiilivalikko"
          >
            {NAV_ITEMS.map((item) => {
              const active = isNavActive(location.pathname, item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block border-b border-white/10 px-6 py-3 text-base font-medium transition-dreamy ${
                    active ? 'text-accent-gold' : 'text-surface-cream/90 hover:text-accent-gold'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/auth/login"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-6 py-3 text-base font-medium text-surface-cream/90 transition-dreamy hover:text-accent-gold"
            >
              Kirjaudu
            </Link>
          </nav>
        )}
      </header>

      <main className="flex-1 relative z-10">{children}</main>

      <Footer contactSubject={PUBLIC_CONTACT_SUBJECT} />
    </div>
  );
}
